// src/utils/monitoringLogger.js
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

let lastLogTime = 0;
const THROTTLE_MS = 5000;

/**
 * Log an event to the monitoring Firestore collection.
 * Throttled to max 1 write per 5 seconds per client.
 *
 * @param {'error'|'warning'|'event'} type
 * @param {'firestore'|'auth'|'listener'|'js'|'ux'} category
 * @param {string} message
 * @param {{ userId?: string, userName?: string, roomId?: string, metadata?: object }} context
 */
export const logMonitoringEvent = async (type, category, message, context = {}) => {
  const now = Date.now();
  if (now - lastLogTime < THROTTLE_MS) return;
  lastLogTime = now;

  try {
    await addDoc(collection(db, 'monitoring'), {
      type,
      category,
      message,
      userId: context.userId || null,
      userName: context.userName || null,
      roomId: context.roomId || null,
      metadata: context.metadata || null,
      timestamp: serverTimestamp(),
    });
  } catch (e) {
    // Silently fail — don't create error loops
    console.error('[monitoringLogger] Failed to write:', e.message);
  }
};

/**
 * Install global JS error handlers that report to monitoring.
 * Call once at app startup.
 */
export const installGlobalErrorHandlers = (getUser) => {
  window.onerror = (message, source, lineno, colno, error) => {
    const user = getUser();
    logMonitoringEvent('error', 'js', String(message), {
      userId: user?.id,
      userName: user?.name,
      metadata: { source, lineno, colno, stack: error?.stack?.slice(0, 500) },
    });
  };

  window.onunhandledrejection = (event) => {
    const user = getUser();
    const message = event.reason?.message || String(event.reason);
    logMonitoringEvent('error', 'js', `Unhandled rejection: ${message}`, {
      userId: user?.id,
      userName: user?.name,
      metadata: { stack: event.reason?.stack?.slice(0, 500) },
    });
  };
};
